import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { KycWarning } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/kycWarning/KycWarning';
import { PaymentAdditions } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/PaymentsAdditions';
import {
    ApplePayBannerDescription,
    ApplePayBannerText,
    ApplePayBannerTitle,
    ApplePayBannerWrapper,
    CardInfo,
    CashBalance,
    CashBalanceWrapper,
    DepositTabWrapper,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/DepositProcedure.style';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { PayButton } from './PaysafeTab.style';
import { PaysafeTabState } from './PaysafeTab.state';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { useCommon } from 'src/domains/common/Common';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { RemainingLimit } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimit';
import { DepositHeader } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/Deposit.style';
import { RingFencedFundsMessage } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/ringFencedFundsWrapper/RingFencedFundsWrapper';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { YaspaSegment } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { isMobileVersionEqualOrHigher } from 'src/utils/mobileApp';
import { RingFencedFundsMessageUpdated } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/ringFencedFundsWrapper/RingFencedFundsWrapperUpdated';
import { mapErrorsMessages } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages';
import { ApplePayIcon } from 'src/ui/assets/icons/other/ApplePayIcon';

interface PropsType {
    state: PaysafeTabState;
    isSignup?: boolean;
}

export const PaysafeTab = observer('PaysafeTab', ({ state, isSignup = false }: PropsType): JSX.Element => {
    const common = useCommon();
    const {
        config: {
            showKycStatus,
            rollingNetDepositLimit,
            yaspaMobileAppValidVersion,
            isSaferGambling,
            paymentProviders,
        },
    } = ConfigComponents.get(common);
    const languagesState = LanguagesState.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const envState = EnvironmentState.get(common);
    const configState = ConfigComponents.get(common).config;
    const isMobileApp = envState.isMobileApp();
    const mobileAppVersion = envState.mobileAppVersion;
    const shouldDisplayYaspaForMobileApp = isMobileApp
        ? isMobileVersionEqualOrHigher(mobileAppVersion, yaspaMobileAppValidVersion)
        : true;
    const shouldDisplayYaspa =
        paymentProviders.openBanking?.type === 'yaspa' && paymentProviders.openBanking.allowOnlyWithdraw === false;
    const shouldDisplayApplePayBannerOnIOSorSafari =
        configState.showApplePayBanner === true && (envState.isSafari() === true || envState.isAppIOSWrapper());
    const shouldDisplayRemainingLimit =
        rollingNetDepositLimit === false || state.remainingLimitState.getRemainingLimit === undefined;

    return (
        <>
            {shouldDisplayApplePayBannerOnIOSorSafari && (
                <ApplePayBannerWrapper>
                    <ApplePayIcon />
                    <ApplePayBannerText>
                        <ApplePayBannerTitle>
                            <I18n
                                langKey='signup-journey.applepay.title'
                                defaultText='Apple Pay is here!'
                            />
                        </ApplePayBannerTitle>
                        <ApplePayBannerDescription>
                            <I18n
                                langKey='signup-journey.applepay.description'
                                defaultText='An easy and secure way to deposit.'
                            />
                        </ApplePayBannerDescription>
                    </ApplePayBannerText>
                </ApplePayBannerWrapper>
            )}
            <DepositTabWrapper isSignup={isSignup}>
                {isSignup ? (
                    <DepositHeader data-test='deposit-header'>
                        <I18n
                            langKey='signup-journey.deposit.title'
                            defaultText='Deposit'
                        />
                    </DepositHeader>
                ) : (
                    <CashBalanceWrapper data-test='cash-balance'>
                        <I18n
                            langKey='account.top-up.cash-balance-label'
                            defaultText='Cash balance'
                        />
                        <CashBalance>{state.showBalance}</CashBalance>
                    </CashBalanceWrapper>
                )}

                <Input
                    type='text'
                    maxLength={10}
                    placeholder='0.00'
                    currency={basicDataModel.moneySymbol}
                    label={
                        <I18n
                            langKey='account.top-up.description-label'
                            defaultText='Deposit amount'
                        />
                    }
                    state={state.depositAmount}
                    onChange={state.onChange}
                    onBlur={state.setAmount}
                    colorTheme={isSignup ? 'dark' : 'light'}
                    inputHasBackground={false}
                    dataTest='deposit-amount-input'
                />

                <PaymentAdditions
                    key='payment-additions'
                    currency={basicDataModel.moneySymbol}
                    value={state.depositAmount.value}
                    onChange={state.handleAdditionsChange}
                />

                {shouldDisplayRemainingLimit ? null : (
                    <RemainingLimit
                        isSignup={isSignup}
                        languagesState={languagesState}
                        value={state.remainingLimitState.getRemainingLimit.format(basicDataModel.currency)}
                    />
                )}

                {state.errorMessagesState.errors.length > 0
                    ? state.errorMessagesState.errors.map((error) => mapErrorsMessages(error, languagesState))
                    : null}

                {state.hasRingFencedFunds === false ? (
                    isSaferGambling.available ? (
                        <RingFencedFundsMessageUpdated isSignup={isSignup} />
                    ) : (
                        <RingFencedFundsMessage
                            isMoreShown={state.showMoreRingFenceFlag}
                            toggleShowMore={state.toggleShowMore}
                            isSignup={isSignup}
                        />
                    )
                ) : null}

                <PayButton
                    size='large'
                    disabled={state.isButtonDisabled}
                    onClick={state.submitDepositForm}
                    type='submit'
                    dataTest='deposit-pay-debit-card'
                >
                    <I18n
                        langKey='account.top-up.pay-card-button'
                        defaultText='Pay using debit card'
                    />
                </PayButton>

                <CardInfo data-test='deposit-card-info'>
                    <I18n
                        langKey='account.top-up.top-upcard-info'
                        defaultText='Credit cards are no longer accepted'
                    />
                </CardInfo>
                {shouldDisplayYaspa && shouldDisplayYaspaForMobileApp ? (
                    <YaspaSegment
                        viewType='deposit'
                        depositYaspaPage={state.depositYaspaPage}
                        isSignup={isSignup}
                    />
                ) : null}

                {showKycStatus === true ? <KycWarning isSignup={false} /> : null}
            </DepositTabWrapper>
        </>
    );
});
